import { css } from '@emotion/react'
import { Tooltip, TooltipProps, tooltipClasses } from '@mui/material'
import { Theme, styled } from '@mui/material/styles'
import { useEffect, useRef, useState } from 'react'

export const StyledTooltip = styled(({ className, ...props }: TooltipProps) => {
  const [open, setOpen] = useState(props.open)
  const mousePositionRef = useRef({ x: 0, y: 0 })
  const [targetElement, setTargetElement] = useState<Element | null>(null)

  /**
   * Work around missing mouseleave events when scrolling the page with the tooltip open.
   * Because the browser doesn't emit mouseleaves on scroll, the tooltip can get stuck
   * open in some scenarios.
   *
   * These effects track the mouse position and listen for scroll events to determine if
   * the mouse is still open while over the target element. If not, it closes the tooltip.
   *
   * Note that this means that delays on the tooltip will not work as expected. Avoid
   * `enterDelay` and `leaveDelay` props.
   */
  useEffect(() => {
    setOpen(props.open)
  }, [props.open])

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mousePositionRef.current = { x: e.clientX, y: e.clientY }
    }

    window.addEventListener('mousemove', handleMouseMove)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
    }
  }, [])

  useEffect(() => {
    if (!open) return

    const handleScroll = () => {
      const elementUnderMouse = document.elementFromPoint(
        mousePositionRef.current.x,
        mousePositionRef.current.y,
      )
      const isMouseOverTarget =
        elementUnderMouse && targetElement && targetElement.contains(elementUnderMouse)

      if (isMouseOverTarget) return

      setOpen(false)
    }

    window.addEventListener('scroll', handleScroll, true)
    return () => {
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [open, targetElement])

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    setTargetElement(e.currentTarget)
    props.onMouseEnter?.(e)
  }

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    setTargetElement(null)
    props.onMouseLeave?.(e)
  }

  return (
    <Tooltip
      {...props}
      open={open}
      classes={{ popper: className }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    />
  )
})({})

export const chartTooltip = ({ theme }: { theme: Theme }) => css`
  .${tooltipClasses.tooltip} {
    background-color: ${theme.palette.white.main};
    color: #0b2948;
    box-shadow:
      ${theme.shadows[1]},
      inset 0 1px 0 0 ${theme.palette.divider}10;
    font-weight: 400;
    border-radius: ${theme.spacing(1)};
    padding: 0px;
    overflow: hidden;
  }

  .header {
    padding: ${theme.spacing(1, 2)};
    font-weight: 700;
    border-bottom: 1px solid #0b2948cc;
  }
`

export const tooltipContent = ({ theme }: { theme: Theme }) => css`
  font-size: 12px;
  display: flex;
  align-items: stretch;
  border-radius: ${theme.spacing(1)};

  .info {
    box-shadow: inset 0 1px 0 0 ${theme.palette.divider}10;
    padding: ${theme.spacing(1, 2)};
    line-height: 1.3;
    border-radius: ${theme.spacing(0, 1, 1, 0)};
  }
`

export const decoColor = (color: string) => css`
  background-color: ${color};
  padding: 4px;
  box-shadow: inset 0 1px 0 0 #0001;
`
